import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  codeBlockPlugin,
  CodeMirrorEditor,
  codeMirrorPlugin,
  DiffSourceToggleWrapper,
  InsertCodeBlock,
  MDXEditor,
} from "@mdxeditor/editor";
import { StyledMDXEditor } from "./style";
import "@mdxeditor/editor/style.css";
import {
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  diffSourcePlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  InsertThematicBreak,
  ListsToggle,
  CodeToggle,
} from "@mdxeditor/editor";
import useLanguage from "../../hooks/useLanguage";
import useToaster from "../../hooks/useToaster";
import { EditImageModal, EditVideoModal } from "../MediaEditor";
import EditMediaToolbarButtons from "./EditMediaToolbarButtons";
import HoverEditBadge from "./HoverEditBadge";
import EditorContextMenu from "./EditorContextMenu";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";

const INITIAL_MODAL_STATE = { open: false, srcUrl: "", oldSrc: "" };

const MarkdownEditor = ({ value, onChange, style, apiRef, onSelect }) => {
  const [
    {
      code,
      components: { markdownEditor, mediaEditor },
    },
  ] = useLanguage();
  const { addToaster } = useToaster();
  const [error, setError] = useState(null);
  const [imageModal, setImageModal] = useState(INITIAL_MODAL_STATE);
  const [videoModal, setVideoModal] = useState(INITIAL_MODAL_STATE);
  const [hoverMedia, setHoverMedia] = useState(null); // { rect, src, type } | null
  const [contextMenu, setContextMenu] = useState(null); // { x, y, type, src }
  const [pendingDelete, setPendingDelete] = useState(null); // { src, type } | null
  const hoverHideTimerRef = useRef(null);
  const containerRef = useRef(null);
  const editorRef = useRef(null);

  const translate = useCallback(
    (key, defaultValue, interpolations) => {
      return (markdownEditor[key] || defaultValue).replace(
        "{{level}}",
        interpolations?.level
      );
    },
    [code]
  );

  // We currently do not want to support this
  const supportedLanguages = [
    "javascript",
    "js",
    "jsx",
    "typescript",
    "tsx",
    "html",
    "css",
    "json",
    "markdown",
  ];

  useEffect(() => {
    if (error) {
      addToaster({
        message: error,
        severity: "error",
      });
      setError(null);
    }
  }, [error]);

  // Open modals from toolbar — URL collected via prompt is treated as both
  // the source to edit AND the old src for swap-on-save.
  const requestImageEdit = useCallback((url) => {
    setImageModal({ open: true, srcUrl: url, oldSrc: url });
  }, []);

  const requestVideoEdit = useCallback((url) => {
    setVideoModal({ open: true, srcUrl: url, oldSrc: url });
  }, []);

  // Hover + context menu: attach plain DOM listeners to the contentEditable
  // area.
  // - mouseover on <img> shows the floating "Edit" badge in the corner.
  // - mouseout on <img> starts a short hide delay (cancelable so the user
  //   can move the cursor onto the badge without it disappearing).
  // - contextmenu (capture) suppresses the native menu and shows our own,
  //   media-aware menu.
  // The contentEditable element is created by MDXEditor after mount, so we
  // use a MutationObserver to wait for `.mui-mdx-editor` to appear.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let editable = null;

    const mediaTypeOf = (el) => {
      if (!el) return null;
      if (el.tagName === "IMG") return "img";
      if (el.tagName === "VIDEO") return "video";
      return null;
    };

    const handleMouseOver = (e) => {
      const type = mediaTypeOf(e.target);
      if (!type) return;
      if (hoverHideTimerRef.current) {
        clearTimeout(hoverHideTimerRef.current);
        hoverHideTimerRef.current = null;
      }
      const rect = e.target.getBoundingClientRect();
      const src =
        type === "video" ? e.target.currentSrc || e.target.src : e.target.src;
      setHoverMedia({ rect, src, type, el: e.target });
    };

    const handleMouseOut = (e) => {
      if (!mediaTypeOf(e.target)) return;
      if (hoverHideTimerRef.current) {
        clearTimeout(hoverHideTimerRef.current);
      }
      hoverHideTimerRef.current = setTimeout(() => {
        setHoverMedia(null);
        hoverHideTimerRef.current = null;
      }, 200);
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const target = e.target;
      let type = "text";
      let src = null;
      if (target && target.tagName === "IMG") {
        type = "img";
        src = target.src;
      } else if (target && target.tagName === "VIDEO") {
        type = "video";
        src = target.currentSrc || target.src;
      }
      setContextMenu({ x: e.clientX, y: e.clientY, type, src });
    };

    const attach = (el) => {
      if (!el || editable === el) return;
      editable = el;
      el.addEventListener("mouseover", handleMouseOver, { capture: true });
      el.addEventListener("mouseout", handleMouseOut, { capture: true });
      el.addEventListener("contextmenu", handleContextMenu, { capture: true });
    };

    const detach = () => {
      if (!editable) return;
      editable.removeEventListener("mouseover", handleMouseOver, {
        capture: true,
      });
      editable.removeEventListener("mouseout", handleMouseOut, {
        capture: true,
      });
      editable.removeEventListener("contextmenu", handleContextMenu, {
        capture: true,
      });
      editable = null;
    };

    // Try immediately in case the editor is already mounted.
    const existing = container.querySelector(".mui-mdx-editor");
    if (existing) attach(existing);

    const observer = new MutationObserver(() => {
      const el = container.querySelector(".mui-mdx-editor");
      if (el && el !== editable) {
        detach();
        attach(el);
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      detach();
      if (hoverHideTimerRef.current) {
        clearTimeout(hoverHideTimerRef.current);
        hoverHideTimerRef.current = null;
      }
    };
  }, []);

  // Keep badge glued to the image/video as the user scrolls or resizes.
  // The badge is portaled with position: fixed, so its top/left must be
  // re-read from getBoundingClientRect whenever the element's viewport
  // position changes.
  useEffect(() => {
    const el = hoverMedia?.el;
    if (!el) return undefined;
    let raf = 0;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        setHoverMedia((prev) => (prev && prev.el === el ? { ...prev, rect } : prev));
      });
    };
    // capture=true catches scrolls on inner scroll containers too.
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hoverMedia?.el]);

  const cancelHoverHide = useCallback(() => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  }, []);

  const hideHover = useCallback(() => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
    setHoverMedia(null);
  }, []);

  // Markdown swap helpers — replace every occurrence of the old src with the
  // new one in the current markdown string. For toolbar-triggered edits where
  // the URL may not yet exist in the document, we append a new tag instead.
  // MDXEditor consumes `markdown` only on mount; subsequent prop updates are
  // ignored. After we mutate the markdown externally (image/video swap), we
  // must call setMarkdown via the imperative ref so the editor reflects the
  // change.
  const applyMarkdown = useCallback(
    (updated) => {
      editorRef.current?.setMarkdown(updated);
      onChange?.(updated);
    },
    [onChange]
  );

  // Let the parent drive setMarkdown too (the AI assistant applies edits this
  // way). Same reason as above: the `markdown` prop is mount-only.
  useEffect(() => {
    if (apiRef) apiRef.current = { applyMarkdown };
  }, [apiRef, applyMarkdown]);

  // Report the editor's text selection to the parent so the assistant can be
  // pointed at a specific passage. Read on gesture end rather than on
  // `selectionchange`, which fires per character while dragging.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onSelect) return undefined;
    // Report "" as well as text: clicking away collapses the selection, and the
    // parent has to hear about that or it keeps sending a passage the editor is
    // no longer looking at. Only gestures inside the editor reach this, so
    // clicking into the assistant's own input can't clear what was just picked.
    const report = () => {
      const selection = window.getSelection();
      const active =
        selection &&
        !selection.isCollapsed &&
        container.contains(selection.anchorNode);
      onSelect(active ? selection.toString().trim() : "");
    };
    container.addEventListener("mouseup", report);
    container.addEventListener("keyup", report);
    return () => {
      container.removeEventListener("mouseup", report);
      container.removeEventListener("keyup", report);
    };
  }, [onSelect]);

  const replaceOrAppendImage = useCallback(
    (oldSrc, newUrl) => {
      const current = value || "";
      if (oldSrc && current.includes(oldSrc)) {
        applyMarkdown(current.split(oldSrc).join(newUrl));
      } else {
        const sep = current.length && !current.endsWith("\n") ? "\n\n" : "";
        applyMarkdown(`${current}${sep}![](${newUrl})\n`);
      }
    },
    [value, applyMarkdown]
  );

  const replaceOrAppendVideo = useCallback(
    (oldSrc, newUrl) => {
      const current = value || "";
      if (oldSrc && current.includes(oldSrc)) {
        applyMarkdown(current.split(oldSrc).join(newUrl));
      } else {
        const sep = current.length && !current.endsWith("\n") ? "\n\n" : "";
        applyMarkdown(
          `${current}${sep}<video src="${newUrl}" controls></video>\n`
        );
      }
    },
    [value, applyMarkdown]
  );

  // Delete an image or video from the markdown by matching its URL.
  // Strips trailing whitespace and collapses 3+ newlines back to 2.
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const removeImageByUrl = useCallback(
    (url) => {
      if (!url) return;
      const re = new RegExp(
        `!\\[[^\\]]*\\]\\(${escapeRegex(url)}(?:\\s+"[^"]*")?\\)\\s*`,
        "g"
      );
      const next = (value || "").replace(re, "").replace(/\n{3,}/g, "\n\n");
      applyMarkdown(next);
    },
    [value, applyMarkdown]
  );
  const removeVideoByUrl = useCallback(
    (url) => {
      if (!url) return;
      const escaped = escapeRegex(url);
      // <video src="URL" ...></video>  OR  <video ... src="URL" ...></video>  OR self-closing
      const re = new RegExp(
        `<video[^>]*src=["']${escaped}["'][^>]*>(?:[\\s\\S]*?<\\/video>)?\\s*`,
        "g"
      );
      const next = (value || "").replace(re, "").replace(/\n{3,}/g, "\n\n");
      applyMarkdown(next);
    },
    [value, applyMarkdown]
  );

  const handleBadgeEdit = useCallback(() => {
    if (hoverMedia?.src) {
      if (hoverMedia.type === "video") requestVideoEdit(hoverMedia.src);
      else requestImageEdit(hoverMedia.src);
    }
    setHoverMedia(null);
  }, [hoverMedia, requestImageEdit, requestVideoEdit]);

  // Delete actions don't remove immediately; they queue a pending delete and
  // the confirmation dialog finalizes it. Removes only the markdown reference
  // — the underlying file in storage is left untouched.
  const handleBadgeDelete = useCallback(() => {
    if (hoverMedia?.src) {
      setPendingDelete({ src: hoverMedia.src, type: hoverMedia.type });
    }
    setHoverMedia(null);
  }, [hoverMedia]);

  const handleDeleteMedia = useCallback((src, type) => {
    if (!src) return;
    setPendingDelete({ src, type });
    setContextMenu(null);
  }, []);

  const confirmPendingDelete = useCallback(() => {
    if (!pendingDelete) return;
    if (pendingDelete.type === "video") removeVideoByUrl(pendingDelete.src);
    else removeImageByUrl(pendingDelete.src);
    setPendingDelete(null);
  }, [pendingDelete, removeImageByUrl, removeVideoByUrl]);

  const cancelPendingDelete = useCallback(() => setPendingDelete(null), []);

  const handleImageSave = useCallback(
    (newUrl) => {
      replaceOrAppendImage(imageModal.oldSrc, newUrl);
    },
    [imageModal.oldSrc, replaceOrAppendImage]
  );

  const handleVideoSave = useCallback(
    (newUrl) => {
      replaceOrAppendVideo(videoModal.oldSrc, newUrl);
    },
    [videoModal.oldSrc, replaceOrAppendVideo]
  );

  const closeImageModal = useCallback(
    () => setImageModal(INITIAL_MODAL_STATE),
    []
  );
  const closeVideoModal = useCallback(
    () => setVideoModal(INITIAL_MODAL_STATE),
    []
  );

  const plugins = useMemo(
    () => [
      // Core plugins
      headingsPlugin(),
      listsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      linkPlugin(),
      linkDialogPlugin(),
      imagePlugin({
        disableImageResize: true,
        disableImageSettingsButton: true,
      }),
      tablePlugin(),
      codeBlockPlugin({
        defaultLanguage: "javascript",
        codeBlockEditorDescriptors: [
          {
            match: () => true,
            priority: 0,
            Editor: CodeMirrorEditor,
          },
        ],
      }),
      codeMirrorPlugin({
        codeBlockLanguages: supportedLanguages.reduce(
          (acc, lang) => ({
            ...acc,
            [lang]: lang,
          }),
          {}
        ),
      }),
      diffSourcePlugin(),
      // Must be last
      markdownShortcutPlugin(),
      // Toolbar plugin must be last
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <UndoRedo />
            <BoldItalicUnderlineToggles />
            <BlockTypeSelect />
            <InsertThematicBreak />
            <ListsToggle />
            <CodeToggle />
            <InsertCodeBlock />
            <EditMediaToolbarButtons
              onRequestImageEdit={requestImageEdit}
              onRequestVideoEdit={requestVideoEdit}
            />
            <DiffSourceToggleWrapper />
          </>
        ),
      }),
    ],
    [requestImageEdit, requestVideoEdit]
  );

  return (
    <StyledMDXEditor ref={containerRef} style={style}>
      <MDXEditor
        ref={editorRef}
        onError={(error) => setError(error.error)}
        translation={translate}
        markdown={value || ""}
        onChange={onChange}
        contentEditableClassName="mui-mdx-editor"
        plugins={plugins}
      />
      <HoverEditBadge
        rect={hoverMedia?.rect}
        onEdit={handleBadgeEdit}
        onDelete={handleBadgeDelete}
        onMouseEnter={cancelHoverHide}
        onMouseLeave={hideHover}
      />
      <EditorContextMenu
        menu={contextMenu}
        onClose={() => setContextMenu(null)}
        onEditImage={(src) => {
          requestImageEdit(src);
          setContextMenu(null);
        }}
        onEditVideo={(src) => {
          requestVideoEdit(src);
          setContextMenu(null);
        }}
        onDeleteMedia={handleDeleteMedia}
      />
      <EditImageModal
        open={imageModal.open}
        srcUrl={imageModal.srcUrl}
        onClose={closeImageModal}
        onSave={handleImageSave}
      />
      <EditVideoModal
        open={videoModal.open}
        srcUrl={videoModal.srcUrl}
        onClose={closeVideoModal}
        onSave={handleVideoSave}
      />
      <ConfirmDeleteDialog
        open={!!pendingDelete}
        type={pendingDelete?.type}
        strings={mediaEditor.confirmDelete}
        onCancel={cancelPendingDelete}
        onConfirm={confirmPendingDelete}
      />
    </StyledMDXEditor>
  );
};

export default MarkdownEditor;
